import { addMinutes, differenceInSeconds } from 'date-fns';
import { Model } from 'json-api-models';

import { Models, Schemas } from '../models.ts';
import { nullableDate } from '../utils/format.ts';
import { formatTimer } from '../utils/format.ts';

export class QuestionSession extends Model<Schemas['questionSessions'], Models> {
    get startedAtDate() {
        return nullableDate(this.startedAt);
    }

    get updatedAtDate() {
        return nullableDate(this.updatedAt);
    }

    get finishedAtDate() {
        return nullableDate(this.finishedAt);
    }

    get isExam() {
        return this.sessionType === 'exam';
    }

    get finishTime(): Date | null {
        if (!this.timeLimit) return null;
        return addMinutes(new Date(this.startedAt), this.timeLimit);
    }

    secondsRemaining(now: Date): number | null {
        if (!this.timeLimit || !this.finishTime) return null;

        if (!this.startedAt) {
            return this.timeLimit * 60;
        }

        const current = this.finishedAt ? new Date(this.finishedAt) : now;
        return Math.max(0, differenceInSeconds(this.finishTime, current));
    }

    proportionTimeElapsed(now: Date): number | null {
        if (!this.timeLimit) return null;
        if (!this.startedAt) return 0;

        const secondsRemaining = this.secondsRemaining(now);
        if (secondsRemaining === null) return null;

        const duration = this.timeLimit * 60;
        return (duration - secondsRemaining) / duration;
    }

    outOfTime(now: Date): boolean {
        return !!this.timeLimit && !this.finishedAt && (this.proportionTimeElapsed(now) || 0) >= 1;
    }

    timeRemaining(now: Date): string {
        const d = this.secondsRemaining(now);

        if (d === null) {
            return '';
        }

        return formatTimer(d);
    }

    timeElapsed(now: Date): string {
        const current = this.finishedAt ? new Date(this.finishedAt) : now;
        const d = Math.max(0, differenceInSeconds(current, new Date(this.startedAt)));

        return formatTimer(d);
    }

    get questionsRemaining(): number | null {
        return this.questionAttempts?.filter((attempt) => !attempt.answeredAt).length ?? null;
    }
}
